import React, { memo } from 'react';
import { Color, Vector3 } from 'three';

import { EnvelopeLabelPlacement } from '@paragon/contract-library';
import { convertDegreesToRadians, LineSegment2D } from '@paragon/geometry-library';

import { createLayoutToDesignTransform } from '../../../library/layout-geometry/coordinates';
import {
  getLabelOffsetAndAlignment,
  LabelTextAlignment,
} from '../../../library/layout-geometry/truss-envelopes/labelPlacement';
import { SDFText } from '../../text/SDFText';
import { TextAlignX, TextAlignY } from '../../text/types';

const LABEL_SCALE = new Vector3(9, 9, 9);

const LABEL_COLOR = new Color(0xffffff);
const LABEL_OUTLINE_COLOR = new Color(0x000000);

// Maps the placement-derived alignment to the text's horizontal anchor, swapping start/end when the label is flipped
//   180° for readability.
function toTextAlignX(labelAlignment: LabelTextAlignment, rotationFlipped: boolean): TextAlignX {
  const effectiveAlignment = rotationFlipped
    ? labelAlignment === 'start'
      ? 'end'
      : labelAlignment === 'end'
        ? 'start'
        : 'center'
    : labelAlignment;

  return effectiveAlignment === 'start' ? 'left' : effectiveAlignment === 'end' ? 'right' : 'center';
}

interface Props {
  envelopeGuid: string;
  envelopeCenterSegment: LineSegment2D;
  elevation: number;
  componentName: string;
  labelPlacement: EnvelopeLabelPlacement;
}

function propsAreEqual(propsA: Props, propsB: Props) {
  return (
    propsA.envelopeGuid === propsB.envelopeGuid &&
    propsA.envelopeCenterSegment.equals(propsB.envelopeCenterSegment) &&
    propsA.elevation === propsB.elevation &&
    propsA.componentName === propsB.componentName &&
    propsA.labelPlacement === propsB.labelPlacement
  );
}

export const LayoutTrussLabel = memo(function LayoutTrussLabel({
  envelopeGuid,
  envelopeCenterSegment,
  elevation,
  componentName,
  labelPlacement,
}: Props) {
  const { direction } = envelopeCenterSegment;

  let labelRotation = convertDegreesToRadians(direction.angle);
  let rotationFlipped = false;

  // flip non-vertical labels if they are more upside down than not
  if (!direction.isVertical() && (labelRotation > Math.PI / 2 || labelRotation < -Math.PI / 2)) {
    labelRotation += Math.PI;
    rotationFlipped = true;
  }

  const { offset: labelOffset, alignment: labelAlignment } = getLabelOffsetAndAlignment(
    labelPlacement,
    envelopeCenterSegment.length,
  );

  const alignX: TextAlignX = toTextAlignX(labelAlignment, rotationFlipped);
  const alignY: TextAlignY = 'bottom';

  const labelPoint = envelopeCenterSegment.toLine().getPointAlongLine(labelOffset).to3D(elevation);
  const labelTransform = createLayoutToDesignTransform(labelPoint, labelRotation, LABEL_SCALE);

  return (
    <SDFText
      matrixWorld={labelTransform}
      color={LABEL_COLOR}
      outlineWidth={0.08}
      outlineOpacity={1}
      outlineColor={LABEL_OUTLINE_COLOR}
      alignX={alignX}
      alignY={alignY}
      text={componentName}
      instanceKey={`truss-label-${envelopeGuid}`}
    />
  );
}, propsAreEqual);
